import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type CreateOkProgram, type UpdateOkProgram } from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

export const useGetOkProgramsQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['ok-programs'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/ok-programs')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useCreateOkProgramMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (okProgram: CreateOkProgram) => {
      const { data, error } = await authFetch().POST('/ok-programs', {
        body: okProgram
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['ok-programs']
      })
    }
  })
}

export const useUpdateOkProgramMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      okProgramId,
      etag,
      body
    }: {
      okProgramId: number
      etag: string
      body: UpdateOkProgram
    }) => {
      const { data, error } = await authFetch().PUT(
        '/ok-programs/{okProgramId}',
        {
          params: {
            path: { okProgramId },
            header: { 'If-Match': etag }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['ok-programs']
      })
    }
  })
}

export const useDeleteOkProgramMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (okProgramId: number) =>
      await authFetch().DELETE('/ok-programs/{okProgramId}', {
        params: {
          path: { okProgramId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['ok-programs']
      })
    }
  })
}
